const STORAGE_KEY = 'cap.token';
const STATE_KEY = 'cap.state';
const VERIFIER_KEY = 'cap.verifier';
const RETURN_URL_KEY = 'cap.returnUrl';
const TRIED_KEY = 'cap.tried';
const REFRESH_LEEWAY_MS = 60000;

function cfg() {
  return window.customerAccount || {};
}

export function isConfiguredValue(v) {
  return typeof v === 'string' && v.length > 0 && !v.startsWith('no_');
}

function isAllowedOrigin() {
  const allowed = cfg().allowedOrigins || [];
  return allowed.includes(location.origin);
}

function base64url(buf) {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(input) {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hash);
}

function randomString(bytes) {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return base64url(arr);
}

function readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(token) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(token));
  } catch (err) {
    void err;
  }
}

function clearStored() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    void err;
  }
}

function toStoredFormat(tokenResponse) {
  return {
    access_token: tokenResponse.access_token,
    refresh_token: tokenResponse.refresh_token,
    expires_at: Date.now() + (Number(tokenResponse.expires_in) || 0) * 1000
  };
}

async function exchange(code, verifier) {
  const c = cfg();
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: c.clientId,
    redirect_uri: `${location.origin}${c.callbackPath}`,
    code,
    code_verifier: verifier
  });
  const res = await fetch(c.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`token exchange failed: ${res.status} ${text}`);
  }
  const json = await res.json();
  const stored = toStoredFormat(json);
  writeStored(stored);
  return stored;
}

async function refresh(refreshToken) {
  const c = cfg();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: c.clientId,
    refresh_token: refreshToken
  });
  const res = await fetch(c.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body
  });
  if (!res.ok) {
    clearStored();
    throw new Error(`token refresh failed: ${res.status}`);
  }
  const json = await res.json();
  const stored = toStoredFormat(json);
  writeStored(stored);
  return stored;
}

function cleanCodeFromUrl() {
  try {
    const url = new URL(location.href);
    let mutated = false;
    ['cap_code', 'cap_state', 'cap_error', 'cap_error_description'].forEach(p => {
      if (url.searchParams.has(p)) {
        url.searchParams.delete(p);
        mutated = true;
      }
    });
    if (mutated) {
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
    }
  } catch (err) {
    void err;
  }
}

async function consumeAuthCodeFromUrl() {
  const params = new URLSearchParams(location.search);
  const code = params.get('cap_code');
  const state = params.get('cap_state');
  const error = params.get('cap_error');

  if (error) {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    cleanCodeFromUrl();
    return null;
  }

  if (!code || !state) return null;

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  cleanCodeFromUrl();

  if (!expectedState || state !== expectedState) return null;
  if (!verifier) return null;

  try {
    return await exchange(code, verifier);
  } catch (err) {
    void err;
    return null;
  }
}

async function startRedirectLogin() {
  const c = cfg();
  const verifier = randomString(32);
  const challenge = base64url(await sha256(verifier));
  const state = randomString(16);

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(RETURN_URL_KEY, location.href);
  sessionStorage.setItem(TRIED_KEY, '1');

  const url = new URL(c.authorizationEndpoint);
  url.searchParams.set('client_id', c.clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', `${location.origin}${c.callbackPath}`);
  url.searchParams.set('scope', 'openid email customer-account-api:full');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('prompt', 'none');

  location.replace(url.toString());
  return new Promise(() => {});
}

export async function getAccessToken() {
  const stored = readStored();
  if (!stored) return null;
  if (stored.expires_at - Date.now() > REFRESH_LEEWAY_MS) {
    return stored.access_token;
  }
  if (!stored.refresh_token) {
    clearStored();
    return null;
  }
  try {
    const refreshed = await refresh(stored.refresh_token);
    return refreshed.access_token;
  } catch (err) {
    void err;
    return null;
  }
}

export async function bootstrap() {
  const fromCallback = await consumeAuthCodeFromUrl();
  if (fromCallback) {
    sessionStorage.removeItem(TRIED_KEY);
    return fromCallback;
  }

  if (!window.isLoggedIn) return null;
  if (!isAllowedOrigin()) return null;
  const c = cfg();
  if (!isConfiguredValue(c.clientId) || !isConfiguredValue(c.tokenEndpoint) || !isConfiguredValue(c.authorizationEndpoint)) return null;

  const cached = readStored();
  if (cached && cached.expires_at - Date.now() > REFRESH_LEEWAY_MS) {
    return cached;
  }
  if (cached?.refresh_token) {
    try {
      return await refresh(cached.refresh_token);
    } catch (err) {
      void err;
    }
  }

  if (sessionStorage.getItem(TRIED_KEY)) {
    return null;
  }

  await startRedirectLogin();
  return null;
}
